import { BOARD_SIZE } from './constants';
import type { IBattleshipGameState, TShotResult, TShotsGrid } from './types';
import { placeShipsRandom } from './utils/place-ships-random';
import { checkCellHit } from './utils/check-cell-hit';
import { checkAllSunk } from './utils/check-all-sunk';
import { getCellsAroundShip } from './utils/get-cells-around-ship';

function createEmptyShotsGrid(): TShotsGrid {
  return Array.from({ length: BOARD_SIZE }, () =>
    Array.from({ length: BOARD_SIZE }, (): TShotResult => null),
  );
}

function cloneShotsGrid(grid: TShotsGrid): TShotsGrid {
  return grid.map((row) => row.slice());
}

export interface IShootResult {
  success: boolean;
  hit: boolean;
  shipSunk: boolean;
  allSunk: boolean;
  winner: 0 | 1 | null;
  state: IBattleshipGameState;
}

export class BattleshipGame {
  private state: IBattleshipGameState;

  constructor() {
    this.state = {
      ships0: placeShipsRandom(),
      ships1: placeShipsRandom(),
      shotsAt0: createEmptyShotsGrid(),
      shotsAt1: createEmptyShotsGrid(),
      sunkShips0: [],
      sunkShips1: [],
      currentPlayer: 0,
    };
  }

  getState(): IBattleshipGameState {
    return {
      ships0: this.state.ships0.map((ship) => ship.map((c) => ({ ...c }))),
      ships1: this.state.ships1.map((ship) => ship.map((c) => ({ ...c }))),
      shotsAt0: cloneShotsGrid(this.state.shotsAt0),
      shotsAt1: cloneShotsGrid(this.state.shotsAt1),
      sunkShips0: (this.state.sunkShips0 ?? []).map((ship) =>
        ship.map((c) => ({ ...c })),
      ),
      sunkShips1: (this.state.sunkShips1 ?? []).map((ship) =>
        ship.map((c) => ({ ...c })),
      ),
      currentPlayer: this.state.currentPlayer,
    };
  }

  shoot(playerSlot: 0 | 1, row: number, col: number): IShootResult {
    const state = this.state;
    if (playerSlot !== state.currentPlayer) {
      return {
        success: false,
        hit: false,
        shipSunk: false,
        allSunk: false,
        winner: null,
        state: this.getState(),
      };
    }
    if (row < 0 || row >= BOARD_SIZE || col < 0 || col >= BOARD_SIZE) {
      return {
        success: false,
        hit: false,
        shipSunk: false,
        allSunk: false,
        winner: null,
        state: this.getState(),
      };
    }
    const targetShots = playerSlot === 0 ? state.shotsAt1 : state.shotsAt0;
    if (targetShots[row][col] !== null) {
      return {
        success: false,
        hit: false,
        shipSunk: false,
        allSunk: false,
        winner: null,
        state: this.getState(),
      };
    }
    const targetShips = playerSlot === 0 ? state.ships1 : state.ships0;
    const { hit, shipIndex } = checkCellHit(targetShips, row, col);
    const result: TShotResult = hit ? 'hit' : 'miss';
    const newShotsAt0 = cloneShotsGrid(state.shotsAt0);
    const newShotsAt1 = cloneShotsGrid(state.shotsAt1);
    const gridToUpdate = playerSlot === 0 ? newShotsAt1 : newShotsAt0;
    gridToUpdate[row][col] = result;

    let shipSunk = false;
    let allSunk = false;
    let winner: 0 | 1 | null = null;
    let newSunkShips0 = state.sunkShips0 ?? [];
    let newSunkShips1 = state.sunkShips1 ?? [];

    if (hit && shipIndex >= 0) {
      const ship = targetShips[shipIndex];
      shipSunk = checkAllSunk([ship], gridToUpdate);
      if (shipSunk) {
        newSunkShips0 =
          playerSlot === 0 ? newSunkShips0 : [...newSunkShips0, ship];
        newSunkShips1 =
          playerSlot === 0 ? [...newSunkShips1, ship] : newSunkShips1;
        for (const { col: c, row: r } of getCellsAroundShip(ship)) {
          if (gridToUpdate[r][c] === null) {
            gridToUpdate[r][c] = 'miss';
          }
        }
        allSunk = checkAllSunk(targetShips, gridToUpdate);
        if (allSunk) {
          winner = playerSlot;
        }
      }
    }

    const nextPlayer: 0 | 1 =
      allSunk || hit
        ? state.currentPlayer
        : ((1 - state.currentPlayer) as 0 | 1);

    this.state = {
      ...state,
      shotsAt0: newShotsAt0,
      shotsAt1: newShotsAt1,
      sunkShips0: newSunkShips0,
      sunkShips1: newSunkShips1,
      currentPlayer: nextPlayer,
    };

    return {
      success: true,
      hit,
      shipSunk,
      allSunk,
      winner,
      state: this.getState(),
    };
  }

  static createInitialState(): IBattleshipGameState {
    return {
      ships0: placeShipsRandom(),
      ships1: placeShipsRandom(),
      shotsAt0: createEmptyShotsGrid(),
      shotsAt1: createEmptyShotsGrid(),
      sunkShips0: [],
      sunkShips1: [],
      currentPlayer: 0,
    };
  }

  static processShot(
    state: IBattleshipGameState,
    playerSlot: 0 | 1,
    row: number,
    col: number,
  ): IShootResult {
    const game = new BattleshipGame();
    (game as unknown as { state: IBattleshipGameState }).state = {
      ships0: state.ships0.map((s) => s.map((c) => ({ ...c }))),
      ships1: state.ships1.map((s) => s.map((c) => ({ ...c }))),
      shotsAt0: cloneShotsGrid(state.shotsAt0),
      shotsAt1: cloneShotsGrid(state.shotsAt1),
      sunkShips0: (state.sunkShips0 ?? []).map((s) => s.map((c) => ({ ...c }))),
      sunkShips1: (state.sunkShips1 ?? []).map((s) => s.map((c) => ({ ...c }))),
      currentPlayer: state.currentPlayer,
    };
    return game.shoot(playerSlot, row, col);
  }
}
